// ABOUTME: Resolves the app-owned feed proxy URL and parses XML responses safely.
// ABOUTME: Handles the case where a host serves the SPA shell instead of the proxy.

/**
 * Base for the feed proxy endpoints.
 *
 * Defaults to a same-origin `/api`, which is what the Netlify deploy serves via
 * the redirects in netlify.toml. Firebase Hosting cannot rewrite to an arbitrary
 * external URL, so a Firebase build must point this at an origin that does host
 * the functions — our proxy responses send `Access-Control-Allow-Origin: *`
 * precisely so that cross-origin case works.
 *
 * Set VITE_FEED_PROXY_BASE at build time, e.g.
 *   VITE_FEED_PROXY_BASE=https://brizzly.netlify.app/api
 */
const CONFIGURED_BASE = (import.meta.env?.VITE_FEED_PROXY_BASE as string | undefined)?.trim();

export const FEED_PROXY_BASE = (CONFIGURED_BASE || "/api").replace(/\/+$/, "");

export const feedProxyUrl = (name: string) => `${FEED_PROXY_BASE}/${name}`;

export class FeedUnavailableError extends Error {
  constructor(
    message: string,
    readonly detail?: string
  ) {
    super(message);
    this.name = "FeedUnavailableError";
  }
}

/**
 * Fetches a feed through the app-owned proxy and returns a parsed XML document.
 *
 * A SPA-hosting fallback will happily answer /api/* with index.html and a 200,
 * which used to surface as a generic "no items found" — indistinguishable from
 * an empty feed. We detect that explicitly so the UI can say something true.
 */
export async function fetchFeedXml(name: string): Promise<Document> {
  const url = feedProxyUrl(name);
  let res: Response;
  try {
    res = await fetch(url);
  } catch (err) {
    throw new FeedUnavailableError(
      "Couldn't reach the feed service",
      err instanceof Error ? err.message : undefined
    );
  }

  if (!res.ok) {
    throw new FeedUnavailableError(`Feed service returned ${res.status}`);
  }

  const contentType = res.headers.get("content-type") || "";
  const text = await res.text();

  // The tell-tale sign that the host has no proxy configured: the SPA shell
  // came back instead of a feed.
  const looksLikeHtml = contentType.includes("text/html") || /^\s*<!doctype html/i.test(text);
  if (looksLikeHtml) {
    throw new FeedUnavailableError(
      "Feed proxy isn't configured on this host",
      `${url} returned the app shell instead of a feed. On Firebase Hosting, set VITE_FEED_PROXY_BASE to an origin that serves the functions.`
    );
  }

  const xml = new window.DOMParser().parseFromString(text, "application/xml");
  if (xml.querySelector("parsererror")) {
    throw new FeedUnavailableError("Feed response wasn't valid XML");
  }

  return xml;
}
